import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTokenData, removeToken } from '../services/authService';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionCheckInProgress, setSubscriptionCheckInProgress] = useState(false);

  const fetchSubscriptionStatus = async (userId) => {
    if (subscriptionCheckInProgress) return null;
    
    try {
      setSubscriptionCheckInProgress(true);
      const response = await api.get('/creem/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      if (error.response?.status === 401) {
        removeToken();
        setUser(null);
      }
      return null;
    } finally {
      setSubscriptionCheckInProgress(false);
    }
  };

  const logout = useCallback(() => {
    removeToken();
    setUser(null);
  }, []);

  const updateUserData = useCallback(async () => {
    try {
      const tokenData = getTokenData();
      if (!tokenData) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Перевіряємо чи токен не закінчився
      const currentTime = Date.now() / 1000;
      if (tokenData.exp < currentTime) {
        console.warn('Token expired in updateUserData');
        removeToken();
        setUser(null);
        setLoading(false);
        return;
      }

      let role;
      switch (tokenData.roleId) {
        case 1:
          role = 'admin';
          break;
        case 2:
          role = 'moderator';
          break;
        default:
          role = 'user';
      }
      
      const subscriptionData = await fetchSubscriptionStatus(tokenData.userId);
      
      setUser(prevUser => ({
        ...(prevUser || {}),
        id: tokenData.userId,
        username: tokenData.username,
        userHandle: tokenData.userHandle,
        avatarUrl: tokenData.avatarUrl,
        roleId: tokenData.roleId,
        role: role,
        theme: tokenData.theme,
        language: tokenData.language,
        subscription: subscriptionData
      }));
    } catch (error) {
      console.error('Error updating user data:', error);
      if (error.response?.status === 401) {
        removeToken();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    updateUserData();
    
    // Слухаємо подію forceLogout з api interceptor
    const handleForceLogout = (event) => {
      console.warn('Force logout triggered:', event.detail?.reason);
      logout();
    };
    
    window.addEventListener('forceLogout', handleForceLogout);
    
    return () => {
      window.removeEventListener('forceLogout', handleForceLogout);
    };
  }, [updateUserData, logout]);

  useEffect(() => {
    let interval;
    
    if (user?.id) {
      // Оновлюємо статус підписки кожні 5 хвилин
      interval = setInterval(async () => {
        if (!subscriptionCheckInProgress) {
          const subscriptionData = await fetchSubscriptionStatus(user.id);
          if (subscriptionData) {
            setUser(prevUser => ({
              ...prevUser,
              subscription: subscriptionData
            }));
          }
        }
      }, 300000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [user?.id]);

  const value = {
    user,
    loading,
    logout,
    updateUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 